import { NavLink } from "react-router-dom";
import {
  Activity,
  BookOpenText,
  ClipboardCheck,
  FileText,
  FlaskConical,
  Gauge,
  History,
  type LucideIcon,
  Radio,
  Router,
  Settings,
  ShieldCheck,
  Trash2,
  Truck,
  Users,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { useAuth } from "@/auth/AuthProvider";
import { isAdminRole, isRespaiAdmin, isSalesOnly } from "@/lib/permissions";

type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  // chief + respai_admin (department admins).
  adminOnly?: boolean;
  // respai_admin only (RespAi platform staff). Device, gateway-fleet and
  // retention management are platform-operations concerns the chief shouldn't see.
  platformAdminOnly?: boolean;
  // respai_sales + respai_admin (RES-155). Also the ONLY items a sales-only
  // login sees — its whole world is the sandbox surface plus Settings.
  salesVisible?: boolean;
};

const items: NavItem[] = [
  { to: "/overview", label: "Overview", icon: Gauge },
  { to: "/incidents", label: "Incidents", icon: Activity },
  { to: "/personnel", label: "Personnel", icon: Users },
  { to: "/devices", label: "Devices", icon: Radio, platformAdminOnly: true },
  {
    to: "/gateway-fleet",
    label: "Gateway fleet",
    icon: Router,
    platformAdminOnly: true,
  },
  { to: "/apparatus", label: "Apparatus", icon: Truck, platformAdminOnly: true },
  { to: "/reports", label: "Reports", icon: FileText },
  {
    to: "/sop-compliance",
    label: "SOP Compliance",
    icon: ClipboardCheck,
    adminOnly: true,
  },
  {
    to: "/sop-documents",
    label: "SOP Documents",
    icon: BookOpenText,
    adminOnly: true,
  },
  {
    to: "/compliance",
    label: "Compliance",
    icon: ShieldCheck,
    platformAdminOnly: true,
  },
  {
    to: "/retention",
    label: "Retention",
    icon: Trash2,
    platformAdminOnly: true,
  },
  {
    to: "/audit-log",
    label: "Audit log",
    icon: History,
    platformAdminOnly: true,
  },
  {
    to: "/sandbox",
    label: "Sandbox",
    icon: FlaskConical,
    salesVisible: true,
  },
  { to: "/settings", label: "Settings", icon: Settings, salesVisible: true },
];

export function NavSidebar() {
  const { identity } = useAuth();
  const isAdmin = isAdminRole(identity?.role);
  const isPlatformAdmin = isRespaiAdmin(identity?.role);
  const salesOnly = isSalesOnly(identity?.role);
  const visible = items.filter((item) => {
    // A sales-only login gets exactly the sales surface, nothing else.
    if (salesOnly) return item.salesVisible;
    if (item.to === "/sandbox") return isPlatformAdmin;
    if (item.platformAdminOnly) return isPlatformAdmin;
    if (item.adminOnly) return isAdmin;
    return true;
  });
  return (
    <aside className="flex w-60 flex-col border-r border-border bg-background print:hidden">
      <div className="flex h-14 items-center gap-2 border-b border-border px-4">
        {/* Glyph mark — the sanctioned standalone lockup for small spaces.
            Rendered from its own artwork, never recoloured. */}
        <img
          src="/respai-glyph.png"
          alt=""
          aria-hidden="true"
          className="h-6 w-6 shrink-0"
        />
        <span className="font-heading text-sm font-semibold tracking-tight">
          RespAI Admin
        </span>
      </div>
      <nav className="flex-1 space-y-1 p-2">
        {visible.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors",
                isActive
                  ? "bg-secondary font-medium text-secondary-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
              )
            }
          >
            <Icon className="h-4 w-4" />
            {label}
          </NavLink>
        ))}
      </nav>
    </aside>
  );
}
